import _ from "underscore";
import { createEnterHookPromise } from "../../shared/flux/actions/enterHooks";
import { ActionPromises } from "./actionPromises";

export class EnterHookPromises {
  public static allRegions = () => createEnterHookPromise(
    (router, state: IWebStoreState) => !state.regions || !state.regions.data ? ActionPromises.allRegions() : null
  );

  public static allCountries = () => createEnterHookPromise(
    (router, state: IWebStoreState) => !state.countries || !state.countries.data ? ActionPromises.allCountries() : null
  );

  public static allEnquiriesForCustomer() {
    return createEnterHookPromise(() => ActionPromises.allEnquiriesForCustomer());
  }

  public static enquiryAndRelated = () => createEnterHookPromise(route => {
    const enquiryId = parseInt(route.params["enquiryId"]);
    return ActionPromises.enquiryItineraryAndQuoteFromEnquiryId(enquiryId);
  })

  public static messagesByEnquiryId(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.enquiryMessagesByEnquiryId(parseInt(n.params[paramName])));
  }

  public static allLtos = () => createEnterHookPromise((router, s: IAppStoreState) => {
    if (s.user.data && s.user.data.userType === UserType.Administrator && (!s.ltos.data || s.ltos.data.length === 0)) {
      return ActionPromises.allLtos();
    }
  })

  public static ltoEmployeesByLtoId(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.ltoEmployeesByLtoId(parseInt(n.params[paramName])));
  }

  public static ltoEmployeesByUsersLtoId() {
    return createEnterHookPromise<IAppStoreState>((router, state) => {
      if (state.user.data && state.user.data.userType === UserType.LtoEmployee) {
        const ltoUser = state.user.data as ILtoEmployee;
        return ActionPromises.ltoEmployeesByLtoId(ltoUser.lto.id);
      }
    });
  }

  public static ltoById(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.ltoById(parseInt(n.params[paramName])));
  }

  public static ltoByUsersLtoId() {
    return createEnterHookPromise<IAppStoreState>((router, state) => {
      if (state.user.data && state.user.data.userType === UserType.LtoEmployee) {
        const ltoUser = state.user.data as ILtoEmployee;
        return ActionPromises.ltoById(ltoUser.lto.id);
      }
    });
  }

  public static ltoTermsById(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.ltoTermsById(parseInt(n.params[paramName])));
  }

  public static sampleItinerariesByLtoId(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.sampleItinerariesByLtoId(parseInt(n.params[paramName])));
  }

  public static ltoClear() {
    return createEnterHookPromise(() => ActionPromises.clearCurrentLto());
  }

  public static sampleItinerariesByUsersLtoId() {
    return createEnterHookPromise<IAppStoreState>((router, state) => {
      if (state.user.data && state.user.data.userType === UserType.LtoEmployee) {
        const ltoUser = state.user.data as ILtoEmployee;
        return ActionPromises.sampleItinerariesByLtoId(ltoUser.lto.id);
      }
    });
  }

  public static sampleItineraryBySlug(itinParamName: string, ltoParamName?: string) {
    return createEnterHookPromise((n) => {
      const itinIdParam = n.params[itinParamName];
      const ltoIdParam = n.params[ltoParamName];
      return ltoIdParam
        ? ActionPromises.sampleItineraryBySlug(itinIdParam, parseInt(ltoIdParam))
        : ActionPromises.sampleItineraryBySlugByUsersLtoId(itinIdParam);
    });
  }

  /**
   * For each tab on a customer's enquiry
   */
  public static liveEnquiryItineraryAndQuoteFromRoute() {
    return createEnterHookPromise<IAppStoreState>((route, state) => {
      const quoteId = parseInt(route.params["itineraryQuoteId"]);
      if (quoteId && !isNaN(quoteId)) {
        return ActionPromises.enquiryItineraryAndQuoteFromQuoteId(quoteId);
      }

      // Otherwise fetch currentQuoteId from enquiry - handle /:enquiryId/itinerary and /:enquiryId/payment
      const enquiryId = parseInt(route.params["enquiryId"]);
      if (enquiryId && !isNaN(enquiryId)) {

        const enquiry = state.enquiry.data;

        return enquiry != null
          ? ActionPromises.enquiryItineraryAndQuoteFromQuoteId(enquiry.currentQuoteId)
          : ActionPromises.enquiryItineraryAndQuoteFromEnquiryId(enquiryId);
      }
    });
  }

  public static draftEnquiryItineraryById(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.draftEnquiryItineraryById(parseInt(n.params[paramName])));
  }

  public static paymentsForEnquiryId(paramName: string) {
    return createEnterHookPromise((n) =>
      ActionPromises.paymentsForEnquiryId(
        parseInt(n.params[paramName]),
        [PaymentStatus.Done, PaymentStatus.Pending]
      )
    );
  }

  public static allCurrencies = () => createEnterHookPromise((router, state: IWebStoreState) => {
    if (!state.currencies || !state.currencies.data || (state.currencies.data && !_.isArray(state.currencies.data))) {
      return ActionPromises.allCurrencies();
    }
  })

  public static allCardTypes = () => createEnterHookPromise((router, state: IWebStoreState) => {
    if (!state.cardTypes.data || !state.cardTypes.data.length) {
      return ActionPromises.allCardTypes();
    }
  })

  public static getReviewById(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.getReview(parseInt(n.params[paramName])));
  }

  public static getReviewsForAdmin() {
    return createEnterHookPromise(() => ActionPromises.getReviewsPage(undefined, -1));
  }

  public static contentEditorFromUrl = () => createEnterHookPromise(router => {
    const contentId = parseInt(router.params["id"]);
    return !_.isNaN(contentId) ? ActionPromises.getContentForEdit(contentId) : null;
  })

  public static themeEditorFromUrl = () => createEnterHookPromise(router => {
    const themeId = parseInt(router.params["id"]);
    return !_.isNaN(themeId) ? ActionPromises.getThemeForEdit(themeId) : ActionPromises.clearThemeEditor();
  })

  public static getUserPermissions(userId?) {
    return createEnterHookPromise(() => ActionPromises.getUserGlobalPermissions(userId));
  }

  public static getUserProfile() {
    return createEnterHookPromise(
      (routerParams) => ActionPromises.getUserProfile(parseInt(routerParams.params["userId"]))
    );
  }

  public static getSnippets() {
    return createEnterHookPromise(
      () => ActionPromises.getSnippetsPage(1, 100, { sort: SnippetSort.TitleAsc, statuses: [SnippetStatus.Live] })
    );
  }

  public static getTeamProfiles() {
    return createEnterHookPromise(() => ActionPromises.getAboutTeamProfiles());
  }

  public static getBookingStatistics(range: string) {
    return createEnterHookPromise(() => ActionPromises.getBookingStatistics(range));
  }
}
